/**
 * 品質管理 Store | Quality Management Store
 * 管理檢核項目、檢驗記錄、不良記錄
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  InspectionItem,
  InspectionRecord,
  DefectRecord,
  QualityStatistics,
} from '@/types/quality';

// 範例檢核項目資料
const mockInspectionItems: InspectionItem[] = [
  {
    id: 'QI001',
    code: 'DIM001',
    name: '外徑尺寸',
    category: '尺寸',
    method: '量測',
    standard: '50.00±0.05',
    tolerance: '±0.05mm',
    unit: 'mm',
    referenceDoc: 'SPE-P-001',
    productId: 'P001',
    isRequired: true,
    samplingRate: 100,
    criticalLevel: '關鍵',
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'QI002',
    code: 'VIS001',
    name: '外觀檢查',
    category: '外觀',
    method: '目視',
    standard: '無刮痕、無變形',
    referenceDoc: 'SPE-V-001',
    productId: 'P001',
    isRequired: true,
    samplingRate: 10,
    criticalLevel: '重要',
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'QI003',
    code: 'FUN001',
    name: '壓力感測器功能測試',
    category: '功能',
    method: '功能測試',
    standard: '輸出電壓 0-5V，誤差±2%',
    unit: 'V',
    referenceDoc: 'SPE-F-001',
    productId: 'P001',
    isRequired: true,
    samplingRate: 100,
    criticalLevel: '關鍵',
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'QI004',
    code: 'MAT001',
    name: '材料成分分析',
    category: '材料',
    method: '化學分析',
    standard: '符合 SUS304 標準',
    referenceDoc: 'SPE-M-001',
    productId: 'P001',
    isRequired: false,
    samplingRate: 5,
    criticalLevel: '一般',
    notes: '供應商提供材質證明即可',
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  },
];

interface QualityStore {
  // 檢核項目 | Inspection items
  inspectionItems: InspectionItem[];
  addInspectionItem: (item: Omit<InspectionItem, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateInspectionItem: (id: string, item: Partial<InspectionItem>) => void;
  deleteInspectionItem: (id: string) => void;
  getInspectionItemsByProduct: (productId: string) => InspectionItem[];

  // 檢驗記錄 | Inspection records
  inspectionRecords: InspectionRecord[];
  addInspectionRecord: (record: Omit<InspectionRecord, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateInspectionRecord: (id: string, record: Partial<InspectionRecord>) => void;
  getInspectionRecordsByOrder: (orderId: string) => InspectionRecord[];
  getInspectionRecordsByDateRange: (start: string, end: string) => InspectionRecord[];

  // 不良記錄 | Defect records
  defectRecords: DefectRecord[];
  addDefectRecord: (record: Omit<DefectRecord, 'id' | 'reportedAt'>) => void;
  resolveDefectRecord: (id: string, resolution: string) => void;

  // 統計資料 | Statistics
  getQualityStatistics: () => QualityStatistics;
}

export const useQualityStore = create<QualityStore>()(
  persist(
    (set, get) => ({
      // 初始檢核項目資料
      inspectionItems: mockInspectionItems,

      // 檢核項目操作
      addInspectionItem: (item) => {
        const newItem: InspectionItem = {
          ...item,
          id: `QI${Date.now()}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        set((state) => ({
          inspectionItems: [...state.inspectionItems, newItem],
        }));
      },

      updateInspectionItem: (id, item) => {
        set((state) => ({
          inspectionItems: state.inspectionItems.map((i) =>
            i.id === id
              ? { ...i, ...item, updatedAt: new Date().toISOString() }
              : i
          ),
        }));
      },

      deleteInspectionItem: (id) => {
        set((state) => ({
          inspectionItems: state.inspectionItems.filter((i) => i.id !== id),
        }));
      },

      getInspectionItemsByProduct: (productId) => {
        return get().inspectionItems.filter(
          (item) => !item.productId || item.productId === productId
        );
      },

      // 檢驗記錄操作
      inspectionRecords: [],

      addInspectionRecord: (record) => {
        const newRecord: InspectionRecord = {
          ...record,
          id: `QR${Date.now()}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        set((state) => ({
          inspectionRecords: [...state.inspectionRecords, newRecord],
        }));
      },

      updateInspectionRecord: (id, record) => {
        set((state) => ({
          inspectionRecords: state.inspectionRecords.map((r) =>
            r.id === id
              ? { ...r, ...record, updatedAt: new Date().toISOString() }
              : r
          ),
        }));
      },

      getInspectionRecordsByOrder: (orderId) => {
        return get().inspectionRecords.filter((r) => r.orderId === orderId);
      },

      getInspectionRecordsByDateRange: (start, end) => {
        return get().inspectionRecords.filter((r) => {
          const recordDate = new Date(r.inspectionDate);
          return recordDate >= new Date(start) && recordDate <= new Date(end);
        });
      },

      // 不良記錄操作
      defectRecords: [],

      addDefectRecord: (record) => {
        const newRecord: DefectRecord = {
          ...record,
          id: `DR${Date.now()}`,
          reportedAt: new Date().toISOString(),
          resolved: false,
        };
        set((state) => ({
          defectRecords: [...state.defectRecords, newRecord],
        }));
      },

      resolveDefectRecord: (id, resolution) => {
        set((state) => ({
          defectRecords: state.defectRecords.map((d) =>
            d.id === id
              ? { ...d, resolved: true, resolvedAt: new Date().toISOString(), resolution }
              : d
          ),
        }));
      },

      // 統計資料
      getQualityStatistics: () => {
        const records = get().inspectionRecords;
        const total = records.length;

        if (total === 0) {
          return {
            totalInspections: 0,
            passRate: 100,
            defectRate: 0,
            topDefectTypes: [],
            monthlyTrend: [],
          };
        }

        const qualified = records.filter(
          (r) => r.status === '合格' || r.status === '條件合格'
        ).length;

        const passRate = (qualified / total) * 100;
        const defectRate = 100 - passRate;

        // 統計不良類型
        const defectTypeMap = new Map<string, number>();
        records.forEach((record) => {
          if (record.defectTypes) {
            record.defectTypes.forEach((type) => {
              defectTypeMap.set(
                type,
                (defectTypeMap.get(type) || 0) + 1
              );
            });
          }
        });

        const topDefectTypes = Array.from(defectTypeMap.entries())
          .map(([defectType, count]) => ({
            defectType,
            count,
            percentage: (count / total) * 100,
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        // 月度趨勢
        const monthlyMap = new Map<string, { inspections: number; passRate: number; defects: number }>();
        records.forEach((record) => {
          const month = record.inspectionDate.substring(0, 7);
          const current = monthlyMap.get(month) || {
            inspections: 0,
            passRate: 0,
            defects: 0,
          };

          current.inspections++;
          if (record.status === '合格' || record.status === '條件合格') {
            current.passRate += record.passRate;
          }
          if (record.hasDefect) {
            current.defects++;
          }

          monthlyMap.set(month, current);
        });

        const monthlyTrend = Array.from(monthlyMap.entries())
          .map(([month, data]) => ({
            month,
            inspections: data.inspections,
            passRate: data.inspections > 0 ? data.passRate / data.inspections : 100,
            defects: data.defects,
          }))
          .sort((a, b) => a.month.localeCompare(b.month));

        return {
          totalInspections: total,
          passRate: Math.round(passRate * 100) / 100,
          defectRate: Math.round(defectRate * 100) / 100,
          topDefectTypes,
          monthlyTrend,
        };
      },
    }),
    {
      name: 'quality-storage',
    }
  )
);
